
export type ChangelogFeature = {
  title: string
  description: string
  keys?: { label: string; action: string }[]
  proFeature?: boolean
  proHintKey?: string
}

export type ChangelogEntry = {
  epoch: number
  features: ChangelogFeature[]
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    epoch: 1786147200000, // 08.08.26
    features: [
      {
        title: "Blog",
        description: "Retroboard has a blog now. I'll be writing about building it, and about using it for things it was never designed for. First up: how it became my kids' allowance board. Find it in the footer, or at retroboard.net/blog.",
      },
    ],
  },
  {
    epoch: 1781049600000, // 06.10.26
    features: [
      {
        title: "Retroboard Becomes a Business",
        description: "After years as a side project, Retroboard officially becomes a business today. It shares this date with Benjamin Franklin flying his famous kite during a 1752 lightning storm. A fitting omen for any small venture hoping to catch a spark.",
      },
    ],
  },
  {
    epoch: 1778803200000, // 05.15.26
    features: [
      {
        title: "Persistent Polls",
        description: "Pin a poll card so it carries into future sessions. Each time the team weighs in, results are charted alongside prior rounds, handy for tracking team morale, sprint confidence, or any recurring question over time.",
        proFeature: true,
        proHintKey: "pro.pinItem.hint",
      },
    ],
  },
  {
    epoch: 1777420800000, // 04.29.26
    features: [
      {
        title: "Bring Your Own Key Encryption",
        description: "Session content can now be encrypted with a shared passphrase. Open Configure Session (cog icon) and enter a passphrase, and card text, category titles, and poll options are encrypted before saving to the server. The passphrase never leaves your browser. Share it with your team out-of-band so everyone can read the content.",
        proFeature: true,
      },
    ],
  },
  {
    epoch: 1775347200000, // 04.05.26
    features: [
      {
        title: "Templates",
        description: "Quickly configure a board or mix up the format of your next retro session using templates.",
      },
      {
        title: "Data Privacy",
        description: "In addition to being able to shred board and session data, you can now request an export of your data or completely delete your presence from Retroboard. For personal boards all content is deleted, and for boards shared with other users your contributions will be anonymized.",
      },
    ],
  },
  {
    epoch: 1774828800000, // 03.30.26
    features: [
      {
        title: "Avatars",
        description: "Each board member now has a little pixel avatar that can walk, jump, and emote during sessions. Customize from your profile settings.",
        keys: [
          { label: "arrow keys / a,d", action: "move left and right" },
          { label: "space", action: "jump" },
          { label: "h", action: "heart emote" },
          { label: "b", action: "speech bubble emote" },
          { label: "r", action: "raise hand emote" },
        ],
      },
      {
        title: "Updated Presentation Shortcuts",
        description: "Previous and next presentation keybindings changed from arrow keys to [,] and [.] so that arrow keys can control your avatar.",
      },
    ],
  },
  {
    epoch: 1774137600000, // 03.22.26
    features: [
      {
        title: "Retroboard ported to TypeScript",
        description: "The entire application has been rebuilt while maintaining Full Feature Parity™ with the original... If anything important is missing, let me know on github!",
      },
    ],
  },
  {
    epoch: 1670889600000, // 12.13.22
    features: [
      {
        title: "Export Session to CSV",
        description: "Added a new keybindings.",
        keys: [
          { label: "Mac:[cmd + e]", action: "Export current session data to csv." },
        ],
      },
    ],
  },
  {
    epoch: 1668211200000, // 11.12.22
    features: [
      {
        title: "Better Shortcuts",
        description: "Shortcuts shouldn't conflict with system or browser shortcuts that use the shift/alt/meta/cntl keys.",
      },
    ],
  },
  {
    epoch: 1667001600000, // 10.29.22
    features: [
      {
        title: "Change Card Order",
        description: "You can now drag cards vertically to higher or lower positions within their column, as well as across columns.",
      },
      {
        title: "Change Column Order",
        description: "Topic column order can now be updated from the session config modal (cog icon).",
      },
      {
        title: "Prompt Nicknames",
        description: "If a user hasn't set their nickname yet (anon), they will be prompted to do so next time they log in.",
      },
    ],
  },
  {
    epoch: 1631923200000, // 9.18.21
    features: [
      {
        title: "404",
        description: "Navigating to an invalid session url will provide some 404 info.",
      },
    ],
  },
  {
    epoch: 1626652800000, // 7.19.21
    features: [
      {
        title: "Presentation Pacer",
        description: "You can now enable timed presentations via the Session Management modal. This option will add a pacer progress bar to the bottom of the presentation area that can help insure there's time to go over every cards on the board.",
      },
    ],
  },
  {
    epoch: 1626134400000, // 7.13.21
    features: [
      {
        title: "Links",
        description: "Links can now be included in card content.",
      },
    ],
  },
  {
    epoch: 1626048000000, // 7.12.21
    features: [
      {
        title: "Drag Cards Between Columns",
        description: "You can now move cards from one column to another using the drag gesture. Maybe you have (To Do, In Progress, and Complete) columns or something. ;)",
      },
      {
        title: "Session Keybindings",
        description: "There are a bunch of them. But basically, you can do most all session related things with keybindings now:",
        keys: [
          { label: "c", action: "add new column" },
          { label: "1-9", action: "add card to nth column" },
          { label: "p", action: "start presentation" },
          { label: ",", action: "present previous item" },
          { label: ".", action: "present next item" },
          { label: "esc", action: "end presentation" },
        ],
      },
    ],
  },
  {
    epoch: 1625356800000, // 7.4.21
    features: [
      {
        title: "Secret Feature Added",
        description: "Shh... It's a secret!",
      },
    ],
  },
  {
    epoch: 1625097600000, // 7.1.21
    features: [
      {
        title: "Card Supporter Names",
        description: "A title has been added to the supporter count on each card. Hover this count to see the names of collaborators who support what that card has to say.",
      },
      {
        title: "Link to Latest Session",
        description: "In addition to inviting new users to a board, the board link will now take you to that board's latest session.",
      },
    ],
  },
  {
    epoch: 1624320000000, // 6.22.21
    features: [
      {
        title: "Poll Cards",
        description: "You can now add poll options to a session card (to see where your collaborators really stand on things).",
      },
    ],
  },
  {
    epoch: 1624233600000, // 6.21.21
    features: [
      {
        title: "Presentation Mode",
        description: "Press play during a session to start presenting each card on the board.",
      },
    ],
  },
  {
    epoch: 1623888000000, // 6.17.21
    features: [
      {
        title: "The whole thing changed... it's all new!",
        description: "The UI has been rewritten from the ground up, and I hope you enjoy playing with it as much as I've enjoyed rebuilding it. :)",
      },
      {
        title: "Board Links",
        description: "To invite people to a board, you'll now need to provide them with a special invite link (you can find this link on the top of board cards on the landing page). Following a link to a board's session will no longer automatically add you to that board.",
      },
      {
        title: "Edit Topics & Cards",
        description: "You can now edit topics or cards by clicking on them. Topics can be hidden from the edit modal, and made visible again from the session's restore modal.",
      },
      {
        title: "Board Timeline",
        description: "The session list view has been replaced with a timeline of Sessions visible on the landing page. Board owners will have an option to shred individual sessions from within this view.",
      },
      {
        title: "Name Changes",
        description: "Now you can change your name by clicking the \"Logged in as\" button in the top right corner of the app.",
      },
    ],
  },
]

export const latestChangelogEpoch = CHANGELOG[0].epoch
